/**
 * Speedjunkies Global Product Entries & Multiplier Updater
 * Runs globally on every page to fetch active multiplier and keep entries and flashing badges up-to-date,
 * including correct handling of active product overrides.
 */
(function() {
  // Default/fallback multiplier. window.sjScheduledMultiplier is written in the
  // page head by snippets/scripts.liquid from the date schedule in
  // snippets/entries-multiplier-schedule.liquid (shop timezone). 50 is the
  // baseline multiplier and is only used if that global is missing.
  function sjDefaultMultiplier() {
    const scheduled = Number(window.sjScheduledMultiplier);
    return scheduled > 0 ? scheduled : 50;
  }
  window.currentGiveawayMultiplier = window.currentGiveawayMultiplier || sjDefaultMultiplier();
  
  // In-memory cache to prevent redundant API fetches (e.g. on rapid variant swaps or scrolls)
  const entriesCache = {};

  // Helper: Retrieve price in cents for a given label
  function getPriceCents(label) {
    const container = label.closest('[data-product-id]');
    let priceCents = 0;
    const priceCentsAttr = container?.getAttribute('data-price');
    if (priceCentsAttr) {
      priceCents = parseFloat(priceCentsAttr);
    } else {
      const priceDollarsAttr = label.getAttribute('data-price-dollars');
      if (priceDollarsAttr) {
        priceCents = Math.round(parseFloat(priceDollarsAttr) * 100);
      }
    }
    return priceCents;
  }

  // Helper: Retrieve numeric product ID
  function getProductId(label) {
    const container = label.closest('[data-product-id]');
    const productId = container ? container.getAttribute('data-product-id') : null;
    return productId ? productId.split('/').pop() : null;
  }

  // Helper: Apply the entries and badge text to elements
  function applyProductEntry(label, productData, multiplier, priceCents) {
    if (priceCents !== undefined && priceCents !== null) {
      const currentPriceCents = getPriceCents(label);
      if (currentPriceCents !== priceCents) {
        return; // Skip stale updates from race conditions
      }
    }

    label.innerHTML = `+${productData.entries.toLocaleString()} ENTRIES`;
    
    const container = label.closest('[data-product-id]');
    const badge = label.parentElement?.querySelector('[data-multiplier-badge]') || container?.querySelector('[data-multiplier-badge]');
    if (badge) {
      badge.textContent = productData.hasOverride ? productData.label : `${multiplier}X`;
      badge.classList.remove('hidden');
      badge.style.display = 'inline-flex';
    }
  }

  /* ------------------------------------------------------------------
   * Cart entries (Speed Junkies)
   * ------------------------------------------------------------------
   * The cart TOTAL is not a valid basis for entries. Fixed-entry products
   * such as the "$5 ONLY VIP Membership" must always award their fixed
   * amount (1,500) and must never be scaled by the sitewide promotional
   * multiplier. The old code did `cartTotalDollars * multiplier`, which
   * rendered "+500 ENTRIES" for a $5.00 VIP cart while a 100X promo was live.
   *
   * Entries are now summed PER CART LINE. The line data is server-rendered by
   * snippets/sj-cart-entries.liquid into [data-cart-entries-lines] and is then
   * reconciled against Speed Junkies Manager (/apps/api/product-entries),
   * which remains the authoritative source for every line - including the
   * active VIP override (product 8514031747306 -> 1500, multiplier ineligible).
   * ---------------------------------------------------------------- */
  function sjReadCartLines(box) {
    try {
      const raw = box.getAttribute('data-cart-entries-lines');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch (err) {
      return null;
    }
  }

  // Entries for a single cart line. Manager data wins when present: an
  // override is already multiplier-independent on the app side.
  function sjLineEntries(line, multiplier, managerProducts) {
    const qty = Number(line.qty) || 0;
    const managed = managerProducts && managerProducts[String(line.id)];
    if (managed && typeof managed.entries === 'number') {
      return managed.entries * qty;
    }
    const serverValue = Number(line.entries);
    if (!isNaN(serverValue) && serverValue > 0) return serverValue;
    if (line.fixed) return (Number(line.unit) || 0) * qty;
    return Math.round(((Number(line.price) || 0) / 100) * multiplier) * qty;
  }

  function sjRenderCartEntries(box, total, multiplier) {
    const label =
      box.querySelector('[data-cart-entries-label]') ||
      document.querySelector('[data-cart-entries-label]');
    if (!label) return;
    label.innerHTML = `+${Math.round(total).toLocaleString()} ENTRIES`;
    // Reveal the cart multiplier pill too - it ships with the `hidden`
    // class and previously nothing ever removed it.
    const badge =
      label.parentElement?.querySelector('[data-multiplier-badge]') ||
      box.querySelector('[data-multiplier-badge]');
    if (badge) {
      badge.textContent = `${multiplier}X`;
      badge.classList.remove('hidden');
      badge.style.display = 'inline-flex';
    }
  }

  async function updateCartEntries(multiplier) {
    const boxes = document.querySelectorAll('[data-cart-total-dollars]');
    if (!boxes.length) return;

    let lines = null;
    boxes.forEach((box) => {
      if (!lines) lines = sjReadCartLines(box);
    });

    if (!lines) {
      // Legacy fallback: markup without per-line data (cached HTML).
      boxes.forEach((box) => {
        const cartTotalDollars = parseFloat(box.getAttribute('data-cart-total-dollars'));
        if (!isNaN(cartTotalDollars)) {
          sjRenderCartEntries(box, cartTotalDollars * multiplier, multiplier);
        }
      });
      return;
    }

    // 1) Immediate, flicker-free total from the server-rendered line data.
    const localTotal = lines.reduce(function (sum, line) {
      return sum + sjLineEntries(line, multiplier, null);
    }, 0);
    boxes.forEach((box) => sjRenderCartEntries(box, localTotal, multiplier));
    if (!lines.length) return;

    // 2) Reconcile every line against Speed Junkies Manager.
    try {
      const queries = [...new Set(lines.map((l) => `${l.id}:${l.price}`))];
      const res = await fetch(`/apps/api/product-entries?products=${encodeURIComponent(queries.join(','))}`);
      if (!res.ok) return;
      const data = await res.json();
      if (!data || !data.success || !data.products) return;
      const managerTotal = lines.reduce(function (sum, line) {
        return sum + sjLineEntries(line, multiplier, data.products);
      }, 0);
      boxes.forEach((box) => sjRenderCartEntries(box, managerTotal, multiplier));
    } catch (err) {
      console.error('[ProductEntriesUpdater] Failed to reconcile cart entries with Manager:', err);
    }
  }

  // Primary Update function
  async function updateGlobalProductEntries(multiplier) {
    const labels = document.querySelectorAll('[data-product-entries-label]');
    if (labels.length === 0) {
      // Cart page / cart drawer have no product-entry labels, but the cart
      // entries terminal still needs to be recalculated.
      updateCartEntries(multiplier);
      return;
    }

    const productQueries = [];
    const labelMapping = [];

    labels.forEach(label => {
      const cleanId = getProductId(label);
      const priceCents = getPriceCents(label);

      if (cleanId && priceCents >= 0) {
        const cacheKey = `${cleanId}:${priceCents}`;

        if (entriesCache[cacheKey]) {
          applyProductEntry(label, entriesCache[cacheKey], multiplier, priceCents);
        } else {
          productQueries.push(`${cleanId}:${priceCents}`);
          labelMapping.push({ label, cleanId, priceCents, cacheKey });
        }
      } else {
        const priceDollars = parseFloat(label.getAttribute('data-price-dollars'));
        if (!isNaN(priceDollars)) {
          // No [data-product-id] wrapper (e.g. featured / recommended carousels).
          // Go through applyProductEntry so the multiplier badge is revealed too,
          // instead of only rewriting the entries label.
          applyProductEntry(label, {
            entries: Math.round(priceDollars * multiplier),
            label: `${multiplier}X`,
            hasOverride: false
          }, multiplier);
        }
      }
    });

    if (productQueries.length > 0) {
      try {
        const uniqueQueries = [...new Set(productQueries)];
        const productsQueryStr = uniqueQueries.join(',');
        const res = await fetch(`/apps/api/product-entries?products=${encodeURIComponent(productsQueryStr)}`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.success && data.products) {
            labelMapping.forEach(({ label, cleanId, priceCents, cacheKey }) => {
              const productData = data.products[cleanId];
              if (productData) {
                entriesCache[cacheKey] = productData;
                applyProductEntry(label, productData, multiplier, priceCents);
              } else {
                const fallbackData = {
                  entries: Math.round((priceCents / 100.0) * multiplier),
                  label: `${multiplier}X`,
                  hasOverride: false
                };
                entriesCache[cacheKey] = fallbackData;
                applyProductEntry(label, fallbackData, multiplier, priceCents);
              }
            });
            updateCartEntries(multiplier);
            return;
          }
        }
      } catch (err) {
        console.error('[ProductEntriesUpdater] Failed to fetch product overrides:', err);
      }

      // Fallback for API failure
      labelMapping.forEach(({ label, priceCents }) => {
        const fallbackData = {
          entries: Math.round((priceCents / 100.0) * multiplier),
          label: `${multiplier}X`,
          hasOverride: false
        };
        applyProductEntry(label, fallbackData, multiplier, priceCents);
      });
    }

    updateCartEntries(multiplier);
  }

  // Expose globally so giveaway console or other components can leverage it
  window.updateGlobalProductEntries = updateGlobalProductEntries;

  // Global listener for variant changes
  document.addEventListener('variant:update', () => {
    setTimeout(() => {
      if (window.currentGiveawayMultiplier) {
        updateGlobalProductEntries(window.currentGiveawayMultiplier);
      }
    }, 50); // Minor tick to ensure DOM is swapped by theme price components
  });

  // Global listener for cart updates to keep cart entries synchronized
  document.addEventListener('cart:update', () => {
    setTimeout(() => {
      if (window.currentGiveawayMultiplier) {
        updateGlobalProductEntries(window.currentGiveawayMultiplier);
      }
    }, 100); // 100ms tick to let AJAX section rendering swap elements
  });

  // Global listener for discount updates
  document.addEventListener('discount:update', () => {
    setTimeout(() => {
      if (window.currentGiveawayMultiplier) {
        updateGlobalProductEntries(window.currentGiveawayMultiplier);
      }
    }, 100);
  });

  // Run initial update on load
  document.addEventListener('DOMContentLoaded', () => {
    updateGlobalProductEntries(window.currentGiveawayMultiplier);
  });
  
  // Run immediately as well
  updateGlobalProductEntries(window.currentGiveawayMultiplier);

  // Fetch current entry multiplier from API
  async function fetchMultiplier() {
    try {
      const multRes = await fetch('/apps/api/current-multiplier');
      if (multRes.ok) {
        const multData = await multRes.json();
        if (multData && multData.success) {
          const multiplier = multData.totalMultiplier || multData.baseMultiplier || sjDefaultMultiplier();
          window.currentGiveawayMultiplier = multiplier;
          updateGlobalProductEntries(multiplier);
        }
      }
    } catch (err) {
      console.error('[ProductEntriesUpdater] Failed to fetch current multiplier:', err);
    }
  }

  /* ------------------------------------------------------------------
   * Keep entries + multiplier badges in sync with content that is
   * rendered AFTER the initial page load: pagination, infinite scroll,
   * facet filtering, quick-add drawers, the Section Rendering API and
   * theme-editor re-renders.
   * Previously the updater only ran once, so any product card that
   * appeared later kept its server-rendered placeholder and never got
   * the live multiplier badge.
   * ---------------------------------------------------------------- */
  let rerunTimer = null;
  function scheduleEntriesRerun() {
    if (rerunTimer) return;
    rerunTimer = setTimeout(function () {
      rerunTimer = null;
      updateGlobalProductEntries(window.currentGiveawayMultiplier);
    }, 60);
  }
  window.scheduleEntriesRerun = scheduleEntriesRerun;

  function startEntriesObserver() {
    if (window.__sjEntriesObserver || !document.body) return;
    const observer = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const added = mutations[i].addedNodes;
        for (let j = 0; j < added.length; j++) {
          const node = added[j];
          if (!node || node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches('[data-product-entries-label]')) ||
            (node.querySelector && node.querySelector('[data-product-entries-label]'))
          ) {
            scheduleEntriesRerun();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.__sjEntriesObserver = observer;
  }

  if (document.body) {
    startEntriesObserver();
  } else {
    document.addEventListener('DOMContentLoaded', startEntriesObserver);
  }

  document.addEventListener('shopify:section:load', scheduleEntriesRerun);
  document.addEventListener('shopify:section:unload', scheduleEntriesRerun);
  document.addEventListener('shopify:block:select', scheduleEntriesRerun);

  fetchMultiplier();
})();
